import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Camera, Upload, CheckCircle, XCircle, AlertCircle, Sparkles, ArrowRight, Bookmark, RotateCcw } from 'lucide-react';
import axios from 'axios';
import SectionHeader from './SectionHeader';
import { getChatRequestUrl } from '../config';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../supabase';
import { TypingFact } from './WaitingEntertainment';
// 2026-06-08: redesigned results view ported from compare-mock (aa0efdc).
// Used as an early-return when we have a successful analysis result; upload
// / error / loading states still flow through the existing layout below.
import ProductAnalysisRedesign from './ProductAnalysisRedesign';
import SavePicker from './SavePicker';
import { IMAGE_ROLES } from '../utils/imageJourney';
import { trackOutcomeJourneyFailed, trackOutcomeResultDelivered, trackProductAnalysisBasisSelected } from '../utils/analytics';

// Route via getChatRequestUrl() which prefers the Function URL (no 29s cap).
// Hitting API Gateway directly here was causing 504s on cold-start product
// analyses (~30s) — Lambda finished fine but API Gateway gave up at 29s and
// the user saw "Failed to analyze product". Same migration that ChatComponent
// + ProductComparisonView already did.
const API_URL = getChatRequestUrl();

const ProductAnalysisView = ({ onBack, existingProduct, onSave, onContinueToChat, userId, customLists = [], targetList = null, createCustomList = null, previewResult = null, onGatedAction }) => {
  const { getAuthSession } = useAuth();
  const uploadInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploadedImage, setUploadedImage] = useState(existingProduct?.image || null);
  const [productImageUrl, setProductImageUrl] = useState(existingProduct?.image || null); // Public URL from storage
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(existingProduct?.cachedResult || null);
  const [showSaveOptions, setShowSaveOptions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profileAvailable, setProfileAvailable] = useState(null);
  const [productContext, setProductContext] = useState(null);
  const [showQuickQuestions, setShowQuickQuestions] = useState(false);
  const [quickAnswers, setQuickAnswers] = useState({ skin_type: '', concern: '', sensitivities: '' });
  const [error, setError] = useState(null); // ✅ FIXED: Add error state
  const [success, setSuccess] = useState(null); // ✅ FIXED: Add success state
  const addedSummaryToChatRef = useRef(false);
  // Hotfix D: inline create-new-list flow inside the save picker.
  const [creatingNewList, setCreatingNewList] = useState(false);
  const [newListNameInput, setNewListNameInput] = useState('');
  const [createListError, setCreateListError] = useState('');
  const [isCreatingList, setIsCreatingList] = useState(false);

  // Slot capacity: createCustomList only succeeds if a custom1/custom2 slot
  // is still default-named AND empty. We compute "has a free slot" from the
  // customLists prop we receive (which is built upstream from showCustom1/
  // showCustom2 — i.e. it omits unnamed empty slots). Fewer than 2 entries
  // means at least one slot is free.
  const hasFreeCustomSlot = (customLists?.length ?? 0) < 2;

  const handleCreateAndSaveToNewList = async () => {
    setCreateListError('');
    const trimmed = newListNameInput.trim();
    if (!trimmed) { setCreateListError('Name cannot be empty.'); return; }
    if (trimmed.length > 50) { setCreateListError('Name must be 50 characters or fewer.'); return; }
    if (!createCustomList) { setCreateListError('Cannot create lists right now.'); return; }
    setIsCreatingList(true);
    try {
      const newKey = await createCustomList(trimmed);
      if (!newKey) {
        setCreateListError("You've reached the limit (2 custom lists). Delete one to add a new list.");
        return;
      }
      setCreatingNewList(false);
      setNewListNameInput('');
      // Now save the product to the newly created list
      await handleSaveProduct(newKey);
    } finally {
      setIsCreatingList(false);
    }
  };

  const handleImageUpload = (event) => {
    if (onGatedAction?.()) {
      event.target.value = '';
      return;
    }
    const file = event.target.files?.[0];
    if (file) {
      setError(null);
      setProductContext(null);
      setShowQuickQuestions(false);
      const reader = new FileReader();
      reader.onloadend = async () => {
        setUploadedImage(reader.result);
        setIsAnalyzing(false);
        setProfileAvailable(null);
        try {
          const session = await getAuthSession();
          const profileUserId = session?.userId || userId;
          if (!profileUserId) {
            setProfileAvailable(false);
            return;
          }
          const { data } = await supabase
            .from('user_profiles')
            .select('skin_type, skin_tone, undertone, primary_concerns, sensitivities')
            .eq('user_id', profileUserId)
            .limit(1);
          const profile = data?.[0];
          setProfileAvailable(Boolean(profile && (
            profile.skin_type || profile.skin_tone || profile.undertone ||
            profile.primary_concerns?.length || profile.sensitivities?.length
          )));
        } catch (profileError) {
          console.warn('Could not check saved skin profile:', profileError);
          setProfileAvailable(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const beginAnalysis = (context) => {
    if (onGatedAction?.()) return;
    trackProductAnalysisBasisSelected(context?.basis || 'saved_profile');
    setError(null);
    setProductContext(context);
    setShowQuickQuestions(false);
    if (previewResult) {
      setAnalysisResult({ ...previewResult, personalizationBasis: context?.basis || 'saved_profile' });
      return;
    }
    setIsAnalyzing(true);
  };

  const triggerUploadInput = () => {
    if (onGatedAction?.()) return;
    uploadInputRef.current?.click();
  };

  const triggerCameraInput = () => {
    if (onGatedAction?.()) return;
    cameraInputRef.current?.click();
  };

  const handleAnalyze = useCallback(async () => {
    if (onGatedAction?.()) return;
    if (!uploadedImage) return;

    try {
      setIsAnalyzing(true);

      // ✅ FIXED: Get fresh authentication token
      const { tokens } = await getAuthSession();
      const accessToken = tokens?.accessToken;
      
      if (!accessToken) {
        throw new Error('Authentication required. Please refresh the page and sign in again.');
      }

      // ✅ Upload product image to Supabase Storage (product-images bucket)
      let productImageUrl = null;
      
      // Compress before use — a full-res phone photo's base64 can exceed the Lambda
      // Function URL's ~6MB payload cap, which 413s the request before the handler runs
      // (the "couldn't read" bug: zero backend logs). Resize to <=1600px, re-encode 0.8 JPEG.
      const _srcDataUrl = uploadedImage.startsWith('data:') ? uploadedImage : `data:image/jpeg;base64,${uploadedImage}`;
      const _compressedDataUrl = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          let w = img.width, h = img.height;
          const maxDim = 1600;
          if (w > maxDim || h > maxDim) { const s = maxDim / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          try { c.getContext('2d').drawImage(img, 0, 0, w, h); resolve(c.toDataURL('image/jpeg', 0.8)); }
          catch { resolve(_srcDataUrl); }
        };
        img.onerror = () => resolve(_srcDataUrl);
        img.src = _srcDataUrl;
      });
      // Extract base64 data (needed for both upload and AI analysis)
      const base64Data = _compressedDataUrl.includes('base64,')
        ? _compressedDataUrl.split('base64,')[1]
        : _compressedDataUrl;
      
      try {
        // Convert base64 to blob for upload
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        
        // Upload to product-images bucket (public)
        const fileName = `${userId}/${Date.now()}_product.jpg`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('product-images')
          .upload(fileName, blob, {
            cacheControl: '3600',
            upsert: false,
            contentType: 'image/jpeg',
          });
        
        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error('Failed to upload product image');
        }
        
        // Get public URL (no signed URL needed - bucket is public)
        const { data: publicUrlData } = supabase.storage
          .from('product-images')
          .getPublicUrl(fileName);
        
        productImageUrl = publicUrlData.publicUrl;
        setProductImageUrl(productImageUrl); // Store in state for later use
        console.log('✅ Product image uploaded:', productImageUrl);
      } catch (uploadException) {
        console.error('⚠️ Failed to upload product image:', uploadException);
        // Continue with analysis even if image upload fails
        console.log('⚠️ Continuing analysis without stored image');
      }
      
      // Send image URL to backend for analysis (along with base64 for AI analysis)
      const response = await axios.post(API_URL, {
        userId: userId,
        message: 'Analyze this product',
        images_data: [base64Data], // For AI analysis
        image_urls: [productImageUrl], // For storage reference
        analysis_type: 'product',
        image_role: IMAGE_ROLES.PRODUCT_REFERENCE,
        user_prompt: 'Analyze this product for me',
        product_context: productContext || { basis: 'saved_profile' },
        accessToken: accessToken
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.status === 401) {
        throw new Error('Session expired. Please refresh the page and sign in again.');
      }

      console.log('✅ Analysis response:', response.data);

      // ✅ FIXED: Parse backend response correctly
      // Backend returns: { analysis_type: 'product', product_data: {...} }
      // OR for chat messages that include images: structured reply object
      
      let productData = null;
      
      // Check if backend returned structured product_data (from image analysis)
      if (response.data.product_data) {
        productData = response.data.product_data;
        console.log('📦 Found product_data in response:', productData);
      }
      // Check if it's embedded in productAnalysis wrapper
      else if (response.data.productAnalysis?.analysis) {
        productData = response.data.productAnalysis.analysis;
        console.log('📦 Found productAnalysis wrapper:', productData);
      }
      // Fallback: check reply object (conversational response)
      else if (response.data.reply && typeof response.data.reply === 'object') {
        productData = response.data.reply;
        console.log('📦 Found product data in reply object:', productData);
      }

      if (productData) {
        // Transform backend data structure to our UI format
        // ── Defensive unpack for ingredient_insight ──────────────────────
        // The dev backend WIP started returning ingredient_insight as a
        // JSON-encoded object containing {key_ingredients, insight,
        // formulation_bullets} instead of a plain paragraph. The frontend
        // then dumped the JSON blob as raw text in the Formulation section.
        // This unpacker detects that shape and spreads the nested fields
        // into the expected top-level slots. Falls through cleanly when
        // ingredient_insight is the legacy plain-string shape.
        const _insightUnpacked = (() => {
          const raw = productData.ingredient_insight;
          if (typeof raw === 'string' && raw.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(raw);
              return {
                insightText: typeof parsed.insight === 'string' ? parsed.insight : '',
                bullets: Array.isArray(parsed.formulation_bullets) ? parsed.formulation_bullets : null,
                extraKeys: Array.isArray(parsed.key_ingredients) ? parsed.key_ingredients : null,
              };
            } catch (e) {
              console.warn('ingredient_insight looked like JSON but failed to parse:', e);
            }
          }
          return {
            insightText: typeof raw === 'string' ? raw : '',
            bullets: null,
            extraKeys: null,
          };
        })();

        const personalizationBasis = productData.personalization_basis || productContext?.basis || 'saved_profile';
        const isFormulaOnly = personalizationBasis === 'formula_only';
        const personalizationContext = personalizationBasis === 'quick_answers'
          ? {
              skinType: productContext?.skin_type || '',
              concern: productContext?.concern || '',
              sensitivities: productContext?.sensitivities || '',
            }
          : null;
        const formattedResult = {
          product: {
            name: productData.name || productData.productName || productData.product_name || 'Product',
            brand: productData.brand || productData.productBrand || productData.product_brand || 'Brand',
            category: productData.category || productData.product_type || 'Skincare',
            price: productData.price || '',
            catalogProductId: productData.catalog_product_id || null,
            ingredientsFull: productData.ingredients_full || '',
            formulaSource: productData.formula_source || '',
            keyIngredients: productData.ingredients_highlight
              ? (typeof productData.ingredients_highlight === 'string'
                  ? productData.ingredients_highlight.split(/(?<!\d),(?!\d)/).map(i => i.trim()).filter(Boolean)
                  : productData.ingredients_highlight)
              : (productData.key_ingredients?.length ? productData.key_ingredients : (_insightUnpacked.extraKeys || [])),
          ingredient_insight: _insightUnpacked.insightText,
          formulation_bullets: Array.isArray(productData.formulation_bullets) && productData.formulation_bullets.length
            ? productData.formulation_bullets
            : (_insightUnpacked.bullets || [])
          },
          matchScore: productData.score || productData.compatibility_score || 80,  // Computed by rubric (server)
          scoring: (productData.scoring && typeof productData.scoring === 'object') ? productData.scoring : null,
          sensitivityHits: Array.isArray(productData.sensitivity_hits) ? productData.sensitivity_hits : [],
          confidenceNote: productData.confidence_note || '',
          personalizationBasis,
          // Keep the answers with this analysis so a reopened result has the
          // same basis. They do not silently overwrite the user's canonical
          // profile; the screen explicitly says they apply to this result.
          personalizationContext,
          analysis: {
            compatibility: isFormulaOnly ? {} : {
              skinType: productData.compatibility || 'good',
              skinTone: productData.skin_tone_match || 'good',
              concerns: productData.concerns_match || 'good'
            },
            pros: productData.pros || productData.whatsGreat || (isFormulaOnly ? [] : ['Suitable for your skin type']),
            cons: productData.concerns || productData.cons || [],
            alternatives: productData.alternatives || []
          },
          recommendation: {
            verdict: productData.compatibility === 'high' ? 'highly-recommended' : 
                     productData.compatibility === 'medium' ? 'recommended' : 
                     productData.compatibility === 'low' ? 'not-recommended' : 'proceed-with-caution',
            reasoning: productData.reasoning || productData.analysis || 'This product may work well for you.',
            usage: productData.best_for || productData.usage || 'Use as directed'
          }
        };
        
        console.log('✅ Formatted result:', formattedResult);
        setAnalysisResult(formattedResult);
        trackOutcomeResultDelivered('product_analysis');

        // Persist to recently-analysed list in localStorage (max 10, includes full result for re-display)
        try {
          const entry = {
            id: `${Date.now()}`,
            name: formattedResult.product?.name || '',
            brand: formattedResult.product?.brand || '',
            category: formattedResult.product?.category || '',
            matchScore: formattedResult.matchScore,
            image: productImageUrl || uploadedImage || null,
            analysedAt: new Date().toISOString(),
            cachedResult: formattedResult, // Store full result so re-opening restores it
          };
          // Key by user so the list survives logout/login and never bleeds between
          // accounts on a shared device.
          const _recentKey = 'sonmi_recent_analyses_' + (userId || 'guest');
          const existing = JSON.parse(localStorage.getItem(_recentKey) || '[]');
          // Deduplicate by name+brand, most recent first
          const deduped = existing.filter(e =>
            !(e.name?.toLowerCase() === entry.name?.toLowerCase() && e.brand?.toLowerCase() === entry.brand?.toLowerCase())
          );
          localStorage.setItem(_recentKey, JSON.stringify([entry, ...deduped].slice(0, 10)));
        } catch {}

      } else {
        console.error('❌ Could not find product data in response:', response.data);
        throw new Error('Invalid response format from backend');
      }

      setIsAnalyzing(false);
    } catch (error) {
      console.error('Error analyzing product:', error);
      trackOutcomeJourneyFailed('product_analysis', 'request_failed');
      setIsAnalyzing(false);
      setProductContext(null);
      // ✅ FIXED: Use error state instead of alert
      setError('Failed to analyze product. Please try uploading a clearer photo.');
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setError(null), 5000);
    }
  }, [uploadedImage, userId, productContext, getAuthSession, onGatedAction]);

  // Auto-analyze when image is uploaded
  useEffect(() => {
    if (uploadedImage && !analysisResult && isAnalyzing) {
      const timer = setTimeout(() => {
        handleAnalyze();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [uploadedImage, isAnalyzing, analysisResult, handleAnalyze]);

  // When analysis completes in catalogue, add summary card to chat so user has it without clicking "Ask follow-up questions"
  useEffect(() => {
    if (!analysisResult || addedSummaryToChatRef.current) return;
    if (typeof window.addProductAnalysisToChat !== 'function') return;
    addedSummaryToChatRef.current = true;
    window.addProductAnalysisToChat({
      type: 'product_followup',
      product: { ...(analysisResult.product || {}), image: productImageUrl },
      matchScore: analysisResult.matchScore,
      analysis: analysisResult.analysis,
      recommendation: analysisResult.recommendation,
      personalizationBasis: analysisResult.personalizationBasis
    });
  }, [analysisResult, productImageUrl]);

  useEffect(() => {
    if (!analysisResult) addedSummaryToChatRef.current = false;
  }, [analysisResult]);

  const handleSaveProduct = async (category, usage_tags = []) => {
    if (!analysisResult || !onSave) return;

    setIsSaving(true);
    try {
      const productToSave = {
        name: analysisResult.product.name,
        brand: analysisResult.product.brand,
        category: analysisResult.product.category, // ✅ FIXED: Use actual product category (e.g., "Serum", "Moisturizer")
        listCategory: category, // ✅ FIXED: Use this for which list to save to ("current" or "wishlist")
        usage_tags, // Shelf #26: carry the picker's usage tags through to the shelf
        image: productImageUrl, // ✅ Use public URL from storage instead of base64
        // 2026-06-09 (Sofia feedback #9): notes was being saved as a verbose
        // "AI Score: 78% - <full reasoning paragraph>" which then dumped onto
        // the Currently Using card as a wall of text. The full reasoning lives
        // in ai_analysis (below) and is reachable by tapping the card; keep
        // notes empty so the list reads clean.
        notes: '',
        ai_analysis: JSON.stringify({
          score: analysisResult.matchScore,
          compatibility: analysisResult.analysis.compatibility.skinType,
          reasoning: analysisResult.recommendation.reasoning,
          pros: analysisResult.analysis.pros,
          concerns: analysisResult.analysis.cons,
          best_for: analysisResult.recommendation.usage,
          ingredients_highlight: analysisResult.product.keyIngredients?.join(', '),
          // #55: benefit/attribute chips + structured ingredients so the redesigned
          // card + detail can render them. Empty until the backend enrichment lands —
          // the UI degrades gracefully.
          benefits: analysisResult.benefits || [],
          ingredients: analysisResult.ingredients || [],
          finish: analysisResult.finish || null,
          coverage: analysisResult.coverage || null,
          spf: analysisResult.spf || null,
          key_ingredients: analysisResult.product.keyIngredients || []
        })
      };

      await onSave(productToSave, category);
      setShowSaveOptions(false);
      // ✅ FIXED: Show success feedback
      setError(null); // Clear any existing errors
      const listName = category === 'current' ? 'Products I Currently Use' : 
                      category === 'wishlist' ? 'Products I Want to Buy' :
                      customLists.find(list => list.id === category)?.name || 'list';
      setSuccess(`Product saved to ${listName}!`);
      // Auto-dismiss success after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving product:', error);
      // ✅ FIXED: Use error state instead of alert
      setError('Failed to save product. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const getRatingLabel = (rating) => {
    const map = { excellent: 'Excellent', high: 'Excellent', good: 'Good', fair: 'Fair', medium: 'Fair', poor: 'Poor', low: 'Poor' };
    return map[rating] || (rating ? rating.charAt(0).toUpperCase() + rating.slice(1) : '');
  };

  const getRatingBadgeStyle = (rating) => {
    if (rating === 'excellent' || rating === 'high' || rating === 'good') return 'text-green-700 border border-green-200 bg-white';
    if (rating === 'fair' || rating === 'medium') return 'text-yellow-700 border border-yellow-200 bg-white';
    return 'text-red-700 border border-red-200 bg-white';
  };

  const getVerdictLabel = (verdict) => {
    if (verdict === 'highly-recommended') return 'Highly Recommended';
    if (verdict === 'recommended') return 'Recommended';
    if (verdict === 'proceed-with-caution') return 'Proceed with Caution';
    return 'Not Recommended';
  };

  const isPositiveVerdict = (v) => v === 'highly-recommended' || v === 'recommended';

  // When the backend couldn't read the product we still build a result object
  // (so the page can navigate), but it's all placeholders. Detect that here so
  // we render an honest error card instead of a fake 80% match.
  const _resName = (analysisResult?.product?.name || '').trim().toLowerCase();
  const _resBrand = (analysisResult?.product?.brand || '').trim().toLowerCase();
  // #1: a real analysis (assessed scoring dimensions) is still usable even when the
  // model couldn't read the product NAME — don't throw it away behind the error card.
  // Only a genuine failure (NO assessed scoring at all — which is also when matchScore
  // is just the 80 default, i.e. a fake number) should show the "couldn't read" state.
  const _hasRealScoring = !!(analysisResult && analysisResult.scoring
    && Object.values(analysisResult.scoring).some((d) => d && d.assessed && typeof d.score === 'number'));
  const isResultError = !!analysisResult && !_hasRealScoring && (
    _resName === 'error' || _resName === 'unable to read clearly' || _resName === 'unknown product'
    || _resBrand === 'error'
    || !!analysisResult.analysis?.error
  );

  // 2026-06-08: when we have a successful analysis result, delegate the entire
  // view to ProductAnalysisRedesign. Upload UI, error UI, and loading UI still
  // flow through the existing return below.
  //
  // 2026-06-09 (Sofia feedback):
  //   - inject the uploaded product image into result.product so the redesign
  //     shows the real photo instead of the 🧴 emoji fallback;
  //   - heart → open the list-picker bottom-sheet (restores the regression
  //     where heart went straight to wishlist without asking which list).
  if (analysisResult && !isResultError) {
    const resultWithImage = {
      ...analysisResult,
      product: {
        ...(analysisResult.product || {}),
        image: analysisResult.product?.image || productImageUrl || uploadedImage,
      },
    };
    return (
      <div className="relative h-full">
        <ProductAnalysisRedesign
          result={resultWithImage}
          onBack={onBack}
          onAnalyseAnother={() => {
            setUploadedImage(null);
            setAnalysisResult(null);
            setProductContext(null);
            setShowQuickQuestions(false);
            setShowSaveOptions(false);
          }}
          onAsk={() => onContinueToChat?.({
            product: analysisResult.product,
            matchScore: analysisResult.matchScore,
            analysis: analysisResult.analysis,
            recommendation: analysisResult.recommendation,
            image: productImageUrl || uploadedImage,
          })}
          onSaveRequest={() => {
            // If user landed here from "Save to <specific list>" flow, honour
            // that and don't re-ask. Otherwise open the picker.
            if (targetList) {
              handleSaveProduct(targetList);
            } else {
              setShowSaveOptions(true);
            }
          }}
        />

        {/* List-picker bottom sheet — restores the pre-redesign "Save to which
            list?" flow Sofia flagged as a regression. Renders over the redesign
            so the user keeps context. */}
        {showSaveOptions && (
          <SavePicker
            product={resultWithImage.product}
            onClose={() => { if (!isSaving) setShowSaveOptions(false); }}
            onSave={({ list_category, usage_tags }) => {
              setShowSaveOptions(false);
              handleSaveProduct(list_category, usage_tags);
            }}
          />
        )}
      </div>
    );
  }

  return (
    // 2026-06-09 (Sofia): self-contained padding now that the MenuSheet p-6
    // wrapper is gone. Upload/error screen gets px-4 pt-4; the RESULT screen
    // (ProductAnalysisRedesign, returned early above) stays edge-to-edge.
    <div className="flex flex-col h-full overflow-hidden px-4 pt-4">
      <SectionHeader title="Product Analysis" subtitle="Formula analysis, with optional personalisation" onBack={onBack} className="mb-3 flex-shrink-0" />

      {/* ✅ FIXED: Error Banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 animate-in fade-in duration-200 flex-shrink-0">
          <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ✅ FIXED: Success Banner */}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2 animate-in fade-in duration-200 flex-shrink-0">
          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-green-800">{success}</p>
          </div>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-600 hover:text-green-800"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4 pb-4">
          {/* Hidden file inputs */}
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageUpload}
            className="hidden"
          />
          
          {!analysisResult ? (
            <div className="space-y-4">
              {/* Upload Section */}
              {!uploadedImage ? (
                <div className="bg-white rounded-2xl shadow-sm p-8">
                  <div className="text-center space-y-5">
                    <div className="w-16 h-16 bg-[#F3D5B5]/50 rounded-full flex items-center justify-center mx-auto">
                      <Camera className="h-8 w-8 text-[#2A324B]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2A324B] mb-1">
                        Upload Product Photo
                      </h3>
                      <p className="text-sm text-[#2A324B]/50">
                        Take a photo of a product or upload from your device
                      </p>
                    </div>
                    <div className="flex flex-col gap-2.5">
                      <button
                        onClick={triggerUploadInput}
                        className="px-4 py-3 bg-[#2A324B] text-[#F3D5B5] rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#2A324B]/90 transition-colors"
                      >
                        <Upload className="h-4 w-4" />
                        Upload Photo
                      </button>
                      <button
                        onClick={triggerCameraInput}
                        className="px-4 py-3 border border-[#E0E0E0] text-[#2A324B] rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#F5F5F5] transition-colors"
                      >
                        <Camera className="h-4 w-4" />
                        Take Photo
                      </button>
                    </div>
                  </div>
                </div>
              ) : !isAnalyzing && !productContext ? (
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden bg-[#F2EAE1] h-36">
                    <img src={uploadedImage} alt="Product to analyse" className="w-full h-full object-cover" />
                  </div>

                  {!showQuickQuestions ? (
                    <div>
                      <p className="text-[13px] font-semibold tracking-[0.14em] uppercase text-[#A97155] mb-2">Product ready</p>
                      <h3 className="font-serif text-[34px] leading-[1.05] text-[#2A1212]">Choose your result</h3>
                      <p className="text-[15px] leading-relaxed text-[#2A1212]/60 mt-3 mb-5">Get a general formula assessment or add the context Son:mi needs to make it personal.</p>

                      <div className="space-y-3">
                        {profileAvailable && (
                          <button type="button" onClick={() => beginAnalysis({ basis: 'saved_profile' })} className="w-full rounded-2xl border border-[#B86E4B] bg-[#F8ECE4] px-4 py-4 text-left">
                            <span className="block text-[16px] font-semibold text-[#2A1212]">Use my saved skin profile</span>
                            <span className="block text-[13px] text-[#2A1212]/55 mt-1">Use your skin type, concerns and sensitivities</span>
                          </button>
                        )}
                        <button type="button" onClick={() => setShowQuickQuestions(true)} className="w-full rounded-2xl border border-[#2A1212]/15 bg-white px-4 py-4 text-left">
                          <span className="block text-[16px] font-semibold text-[#2A1212]">Answer 3 quick questions</span>
                          <span className="block text-[13px] text-[#2A1212]/55 mt-1">Personalise this result without saving a profile</span>
                        </button>
                        <button type="button" onClick={() => beginAnalysis({ basis: 'formula_only' })} className="w-full rounded-2xl border border-[#2A1212]/15 bg-white px-4 py-4 text-left">
                          <span className="block text-[16px] font-semibold text-[#2A1212]">See formula analysis</span>
                          <span className="block text-[13px] text-[#2A1212]/55 mt-1">Ingredients, formulation and general cautions</span>
                        </button>
                      </div>

                      {!profileAvailable && profileAvailable !== null && (
                        <p className="text-[13px] leading-relaxed text-[#2A1212]/50 mt-4">A full skin scan can add visible signals you may not know yet. You can build that baseline later in Skin Journal.</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <button type="button" onClick={() => setShowQuickQuestions(false)} className="text-[13px] font-semibold text-[#8B5138] mb-3">← Back</button>
                      <h3 className="font-serif text-[32px] leading-[1.05] text-[#2A1212]">Make it personal</h3>
                      <p className="text-[14px] text-[#2A1212]/55 mt-2 mb-5">These answers apply only to this result.</p>

                      <fieldset className="mb-5">
                        <legend className="text-[15px] font-semibold text-[#2A1212] mb-2">1. How does your skin usually feel?</legend>
                        <div className="flex flex-wrap gap-2">
                          {['Dry', 'Balanced', 'Combination', 'Oily', 'Not sure'].map((value) => (
                            <button key={value} type="button" aria-pressed={quickAnswers.skin_type === value} onClick={() => setQuickAnswers(a => ({ ...a, skin_type: value }))} className={`px-3 py-2 rounded-full border text-[13px] ${quickAnswers.skin_type === value ? 'bg-[#2A1212] text-white border-[#2A1212]' : 'bg-white text-[#2A1212] border-[#2A1212]/15'}`}>{value}</button>
                          ))}
                        </div>
                      </fieldset>

                      <fieldset className="mb-5">
                        <legend className="text-[15px] font-semibold text-[#2A1212] mb-2">2. What matters most for this product?</legend>
                        <div className="flex flex-wrap gap-2">
                          {['Breakouts', 'Dryness', 'Dark spots', 'Sensitivity', 'Texture', 'No particular concern'].map((value) => (
                            <button key={value} type="button" aria-pressed={quickAnswers.concern === value} onClick={() => setQuickAnswers(a => ({ ...a, concern: value }))} className={`px-3 py-2 rounded-full border text-[13px] ${quickAnswers.concern === value ? 'bg-[#2A1212] text-white border-[#2A1212]' : 'bg-white text-[#2A1212] border-[#2A1212]/15'}`}>{value}</button>
                          ))}
                        </div>
                      </fieldset>

                      <label className="block mb-5">
                        <span className="block text-[15px] font-semibold text-[#2A1212] mb-2">3. Anything you avoid? <span className="font-normal text-[#2A1212]/45">(optional)</span></span>
                        <input value={quickAnswers.sensitivities} onChange={(event) => setQuickAnswers(a => ({ ...a, sensitivities: event.target.value }))} placeholder="e.g. fragrance — or leave blank" className="w-full rounded-2xl border border-[#2A1212]/15 bg-white px-4 py-3 text-[15px] text-[#2A1212] outline-none focus:border-[#B86E4B]" />
                      </label>

                      <button type="button" disabled={!quickAnswers.skin_type || !quickAnswers.concern} onClick={() => beginAnalysis({ basis: 'quick_answers', ...quickAnswers })} className="w-full rounded-full bg-[#2A1212] py-3.5 text-[15px] font-semibold text-white disabled:opacity-35">Analyse this product</button>
                    </div>
                  )}
                </div>
              ) : isAnalyzing ? (
                /* Analyzing State */
                <div className="bg-white rounded-2xl shadow-sm p-8">
                  <div className="text-center space-y-4">
                    <div className="relative rounded-lg overflow-hidden mb-4">
                      <img 
                        src={uploadedImage} 
                        alt="Product being analyzed" 
                        className="w-full h-64 object-cover"
                      />
                      <div className="absolute inset-0 bg-[#2A324B]/10 flex items-center justify-center">
                        <div className="bg-white/95 rounded-full p-6">
                          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#F3D5B5] border-t-[#2A324B]"></div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium text-[#2A324B] mb-1">Analysing Product…</h3>
                      <p className="text-sm text-[#2A324B]/70">
                        {productContext?.basis === 'formula_only' ? 'Son:mi is reading the formula and general cautions' : 'Son:mi is checking how this fits your skin'}
                      </p>
                    </div>
                    {/* Skincare facts during the 20-30s product analysis wait —
                        same component the chat loading state uses, so it's
                        already tuned, swap-on-interval and animates cleanly. */}
                    <div className="pt-4">
                      <TypingFact isLoading={isAnalyzing} />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : isResultError ? (
            /* ── Couldn't read the product — honest error state, no fake score ── */
            <div className="rounded-2xl bg-white border border-[#EAE0D4] p-5 flex gap-3 items-start">
              <div className="w-10 h-10 rounded-xl bg-[#FBF1E6] flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-[#A8742F]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#2A324B]">Couldn't read this product clearly</p>
                <p className="text-xs text-[#2A324B]/65 leading-relaxed mt-1">Try a sharper photo with the label facing the camera and good lighting — or tell Son:mi the product name in chat and it'll analyse from the catalogue.</p>
                <button
                  type="button"
                  onClick={() => { setAnalysisResult(null); setError(null); }}
                  className="mt-3 px-4 py-2 rounded-full text-[13px] font-semibold uppercase tracking-[0.14em]"
                  style={{ background: '#2A1212', color: '#F4ECE3' }}
                >
                  Try another photo
                </button>
              </div>
            </div>
          ) : (
            /* ── Analysis Results ── */
            <div className="space-y-3">

              {/* Product image card with overlay */}
              <div className="rounded-2xl overflow-hidden bg-white border border-[#341C1C]/8 shadow-sm">
                {uploadedImage && (
                  <div className="relative">
                    <img
                      src={uploadedImage}
                      alt="Analyzed product"
                      className="w-full h-52 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a2035]/70 via-transparent to-transparent" />
                    {analysisResult.product.category && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#F3D5B5] text-[#2A324B] text-xs font-medium rounded-full">
                        {analysisResult.product.category}
                      </span>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="font-semibold text-white text-base leading-tight">{analysisResult.product.name}</h3>
                      {analysisResult.product.brand && (
                        <p className="text-white/70 text-sm mt-0.5">{analysisResult.product.brand}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Key ingredients + price */}
                <div className="p-4">
                  {analysisResult.product.keyIngredients?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[13px] font-semibold tracking-widest text-[#2A324B]/40 uppercase mb-2">Key Ingredients</p>
                      <div className="flex flex-wrap gap-1.5">
                        {analysisResult.product.keyIngredients.map((ingredient, idx) => (
                          <span key={idx} className="px-2.5 py-1 text-xs text-[#2A324B] border border-[#2A324B]/15 rounded-full bg-white">
                            {ingredient}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysisResult.product.price && (
                    <p className="text-base font-semibold text-[#2A324B]">{analysisResult.product.price}</p>
                  )}
                </div>
              </div>

              {/* Match score — dark card with circular ring */}
              {(() => {
                const score = analysisResult.matchScore || 0;
                const radius = 36;
                const circ = 2 * Math.PI * radius;
                const offset = circ - (score / 100) * circ;
                const scoreLabel =
                  score >= 85 ? 'Excellent compatibility with your skin profile.' :
                  score >= 70 ? 'Good compatibility with your skin profile.' :
                  score >= 50 ? 'Moderate compatibility with your skin profile.' :
                                'Limited compatibility with your skin profile.';
                return (
                  <div className="rounded-2xl bg-[#2A324B] p-5 flex items-center gap-5">
                    <div className="relative flex-shrink-0">
                      <svg width="90" height="90" className="-rotate-90" aria-hidden>
                        <circle cx="45" cy="45" r={radius} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="6" />
                        <circle cx="45" cy="45" r={radius} fill="none" stroke="#F3D5B5" strokeWidth="6"
                          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
                          style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xl font-bold text-white">{score}%</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-base">Your Match Score</h4>
                      <p className="text-white/55 text-xs mt-0.5 leading-snug">{scoreLabel}</p>
                      <p className="text-[#F3D5B5]/65 text-[13px] mt-2">🔥 Powered by Son:mi</p>
                    </div>
                  </div>
                );
              })()}

              {/* Sensitivity callout — highest-trust signal */}
              {analysisResult.sensitivityHits?.length > 0 && (
                <div className="rounded-2xl bg-[#FEECEC] border border-[#F5C6C6] p-4">
                  <p className="text-sm font-semibold text-[#C0392B] mb-0.5">Heads up — contains {analysisResult.sensitivityHits.join(', ')}</p>
                  <p className="text-xs text-[#C0392B]/80">You flagged this as a sensitivity, so we've lowered the score accordingly.</p>
                </div>
              )}

              {/* Compatibility breakdown — rubric dimensions (the score is the weighted blend of these) */}
              <div className="rounded-2xl bg-white border border-[#341C1C]/8 shadow-sm p-4">
                <h4 className="text-sm font-semibold text-[#2A324B] mb-1">How we scored it</h4>
                <p className="text-[13px] text-[#2A324B]/45 mb-3">Your match score is the weighted average of these.</p>
                {(() => {
                  const DIM_LABELS = {
                    concerns: 'Your concerns', skin_type: 'Skin type', ingredient_safety: 'Ingredient safety',
                    formulation: 'Formulation', shade_match: 'Shade match', texture_finish: 'Texture & finish', wear_coverage: 'Wear & coverage',
                  };
                  const band = (s) => s >= 90 ? { t: 'Excellent', c: 'bg-[#F0F7F0] text-[#3B7A3B]' }
                    : s >= 75 ? { t: 'Good', c: 'bg-[#F0F7F0] text-[#3B7A3B]' }
                    : s >= 55 ? { t: 'Fair', c: 'bg-[#FFF4E5] text-[#B26B00]' }
                    : { t: 'Poor', c: 'bg-[#FEECEC] text-[#C0392B]' };
                  const scoring = analysisResult.scoring;
                  const dims = scoring
                    ? Object.entries(scoring).filter(([, d]) => d && d.assessed && typeof d.score === 'number')
                    : null;
                  if (dims && dims.length) {
                    return (
                      <div className="space-y-3">
                        {dims.map(([key, d]) => {
                          const b = band(d.score);
                          return (
                            <div key={key}>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm text-[#2A324B]">{DIM_LABELS[key] || key}</span>
                                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${b.c}`}>{b.t} · {d.score}</span>
                              </div>
                              {d.reason && <p className="text-[13px] text-[#2A324B]/50 mt-0.5 leading-snug">{d.reason}</p>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  // Fallback: legacy 3-badge view for older responses without rubric scoring
                  return (
                    <div className="space-y-3">
                      {[
                        { label: 'Skin Type', value: analysisResult.analysis.compatibility.skinType },
                        { label: 'Skin Tone', value: analysisResult.analysis.compatibility.skinTone },
                        { label: 'Your Concerns', value: analysisResult.analysis.compatibility.concerns },
                      ].map(({ label, value }) => value ? (
                        <div key={label} className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                            <span className="text-sm text-[#2A324B]">{label}</span>
                          </div>
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 ${getRatingBadgeStyle(value)}`}>
                            <CheckCircle className="h-3 w-3" />
                            {getRatingLabel(value)}
                          </span>
                        </div>
                      ) : null)}
                    </div>
                  );
                })()}
                {analysisResult.confidenceNote && (
                  <p className="text-[13px] text-[#2A324B]/40 mt-3 pt-3 border-t border-[#F0F0F0] leading-snug">{analysisResult.confidenceNote}</p>
                )}
              </div>

              {/* ── Understanding the Formulation — per-ingredient bullets, prose fallback ──
                  AI sometimes embeds **markdown bold** inside the ingredient/note
                  strings even though we ask for plain text. Strip it on render
                  so users don't see literal asterisks. */}
              {(analysisResult.product.formulation_bullets?.length > 0 || analysisResult.product.ingredient_insight) && (
                <div className="rounded-2xl bg-[#F2EAE1]/50 border border-[#F3D5B5]/50 p-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Sparkles className="h-4 w-4 text-[#2A324B]/35 flex-shrink-0" />
                    <h4 className="text-sm font-semibold text-[#2A324B]">Understanding the Formulation</h4>
                  </div>
                  {analysisResult.product.formulation_bullets?.length > 0 ? (
                    <ul className="space-y-2">
                      {analysisResult.product.formulation_bullets.map((b, i) => {
                        const ing = (typeof b.ingredient === 'string' ? b.ingredient : '').replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').trim();
                        const note = (typeof b.note === 'string' ? b.note : '').replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').trim();
                        return (
                          <li key={i} className="flex gap-2 text-[13px] text-[#2A324B]/70 leading-relaxed">
                            <span className="text-[#C8956A] flex-shrink-0 mt-0.5">•</span>
                            <span><span className="font-semibold text-[#2A324B]">{ing}</span>{note ? ` — ${note}` : ''}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-[13px] text-[#2A324B]/70 leading-relaxed">{(analysisResult.product.ingredient_insight || '').replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1')}</p>
                  )}
                </div>
              )}

              {/* ── What this means for you — merged pros + cons ── */}
              {((analysisResult.analysis.pros?.length > 0) || (analysisResult.analysis.cons?.length > 0)) && (
                <div className="rounded-2xl bg-white border border-[#341C1C]/8 shadow-sm p-4">
                  <h4 className="text-sm font-semibold text-[#2A324B] mb-3">What this means for you</h4>
                  <div className="space-y-2.5">
                    {(analysisResult.analysis.pros || []).map((item, idx) => (
                      <div key={`pro-${idx}`} className="flex items-start gap-3">
                        <div className="mt-0.5 w-4 h-4 rounded-full border-2 border-green-400 flex items-center justify-center flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-green-400" />
                        </div>
                        <span className="text-sm text-[#2A324B]/80 leading-snug">{item}</span>
                      </div>
                    ))}
                    {(analysisResult.analysis.cons || []).map((item, idx) => (
                      <div key={`con-${idx}`} className="flex items-start gap-3">
                        <div className="mt-0.5 w-4 h-4 rounded-full border-2 border-amber-400 flex items-center justify-center flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-amber-400" />
                        </div>
                        <span className="text-sm text-[#2A324B]/80 leading-snug">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── How to use it ── */}
              {analysisResult.recommendation?.usage && (
                <div className="rounded-2xl bg-white border border-[#341C1C]/8 shadow-sm p-4">
                  <h4 className="text-sm font-semibold text-[#2A324B] mb-2">How to use it</h4>
                  <p className="text-sm text-[#2A324B]/70 leading-relaxed">{analysisResult.recommendation.usage}</p>
                </div>
              )}

              {/* Alternatives */}
              {analysisResult.analysis.alternatives?.length > 0 && (
                <div className="rounded-2xl bg-white border border-[#341C1C]/8 shadow-sm p-4">
                  <h4 className="text-sm font-semibold text-[#2A324B] mb-3">Alternatives to consider</h4>
                  <div className="space-y-2.5">
                    {analysisResult.analysis.alternatives.map((alt, idx) => (
                      <div key={idx} className="flex items-start gap-2.5">
                        <ArrowRight className="h-3.5 w-3.5 text-[#2A324B]/35 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#2A324B]/75 leading-snug">{alt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Actions — one primary, two quiet secondaries ──
                  "Done" lives in the header (the ✕) so the bottom isn't four
                  competing blocks. Save to Catalogue is the primary action. */}

              {/* Primary: Save to Catalogue.
                  When targetList is provided (user came from add-to-list
                  modal on a specific list), tapping Save goes straight to
                  that list — no second picker. */}
              {!showSaveOptions ? (
                <button
                  onClick={() => {
                    if (targetList) {
                      handleSaveProduct(targetList);
                    } else {
                      setShowSaveOptions(true);
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#2A324B] text-[#F3D5B5] text-sm font-semibold hover:bg-[#2A324B]/90 transition-colors disabled:opacity-50"
                  disabled={isSaving}
                >
                  <Bookmark className="h-4 w-4" />
                  {isSaving
                    ? 'Saving…'
                    : targetList
                      ? `Save to ${customLists.find(l => l.id === targetList)?.name
                          || (targetList === 'current' ? 'Currently Using' : targetList === 'wishlist' ? 'Want to Buy' : 'list')}`
                      : 'Save to Catalogue'}
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#2A324B]/45 px-1">Save to which list?</p>
                  <button onClick={() => handleSaveProduct('current')} disabled={isSaving}
                    className="px-4 py-2.5 rounded-full bg-white border border-[#2A324B]/15 text-[#2A324B] text-sm hover:bg-[#F2EAE1] transition-colors disabled:opacity-50">
                    {isSaving ? 'Saving...' : 'Currently Using'}
                  </button>
                  <button onClick={() => handleSaveProduct('wishlist')} disabled={isSaving}
                    className="px-4 py-2.5 rounded-full bg-white border border-[#2A324B]/15 text-[#2A324B] text-sm hover:bg-[#F2EAE1] transition-colors disabled:opacity-50">
                    {isSaving ? 'Saving...' : 'Want to Buy'}
                  </button>
                  {customLists.map(list => (
                    <button key={list.id} onClick={() => handleSaveProduct(list.id)} disabled={isSaving}
                      className="px-4 py-2.5 rounded-full bg-white border border-[#2A324B]/15 text-[#2A324B] text-sm hover:bg-[#F2EAE1] transition-colors disabled:opacity-50">
                      {isSaving ? 'Saving...' : list.name}
                    </button>
                  ))}

                  {/* Hotfix D: Create-new-list inline. Only show if we have
                      createCustomList wired AND at least one slot is free. */}
                  {createCustomList && hasFreeCustomSlot && (
                    creatingNewList ? (
                      <div className="rounded-2xl border border-[#341C1C]/15 bg-white p-3 space-y-2">
                        <input
                          type="text"
                          value={newListNameInput}
                          onChange={(e) => { setNewListNameInput(e.target.value); setCreateListError(''); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCreateAndSaveToNewList();
                            if (e.key === 'Escape') { setCreatingNewList(false); setNewListNameInput(''); setCreateListError(''); }
                          }}
                          maxLength={50}
                          autoFocus
                          placeholder="New list name"
                          className="w-full px-3 py-2 text-sm border border-[#341C1C]/20 rounded-md focus:outline-none focus:ring-1 focus:ring-[#F3D5B5]"
                        />
                        {createListError && (
                          <p className="text-xs text-amber-700">{createListError}</p>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setCreatingNewList(false); setNewListNameInput(''); setCreateListError(''); }}
                            disabled={isCreatingList || isSaving}
                            className="flex-1 px-3 py-1.5 text-xs rounded-full border border-[#341C1C]/15 text-[#2A324B]/70 hover:bg-gray-50 disabled:opacity-50"
                          >Cancel</button>
                          <button
                            onClick={handleCreateAndSaveToNewList}
                            disabled={isCreatingList || isSaving || !newListNameInput.trim()}
                            className="flex-1 px-3 py-1.5 text-xs rounded-full bg-[#2A324B] text-[#F3D5B5] hover:bg-[#2A324B]/90 disabled:opacity-50"
                          >{isCreatingList ? 'Saving…' : 'Create & save'}</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setCreatingNewList(true); setNewListNameInput(''); setCreateListError(''); }}
                        disabled={isSaving}
                        className="px-4 py-2.5 rounded-full bg-white border border-dashed border-[#2A324B]/30 text-[#2A324B]/70 text-sm hover:bg-[#F2EAE1] transition-colors disabled:opacity-50">
                        + Create new list
                      </button>
                    )
                  )}

                  <button onClick={() => { setShowSaveOptions(false); setCreatingNewList(false); setNewListNameInput(''); setCreateListError(''); }}
                    className="px-4 py-2 rounded-full border border-[#341C1C]/15 text-[#2A324B]/60 text-sm hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                </div>
              )}

              {/* Secondary row: quiet, equal-weight */}
              <div className="flex gap-3">
                {onContinueToChat && (
                  <button
                    onClick={() => onContinueToChat({
                      product: analysisResult.product,
                      matchScore: analysisResult.matchScore,
                      analysis: analysisResult.analysis,
                      recommendation: analysisResult.recommendation,
                      image: productImageUrl || uploadedImage
                    })}
                    className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-full border border-[#2A324B]/20 bg-white text-[#2A324B] text-sm font-medium hover:bg-[#F2EAE1] transition-colors"
                  >
                    <span aria-hidden>💬</span> Ask a question
                  </button>
                )}
                <button
                  onClick={() => { setUploadedImage(null); setAnalysisResult(null); setProductContext(null); setShowQuickQuestions(false); setShowSaveOptions(false); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-full border border-[#2A324B]/20 bg-white text-[#2A324B] text-sm font-medium hover:bg-[#F2EAE1] transition-colors"
                >
                  <RotateCcw className="h-4 w-4" />
                  Analyse another
                </button>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductAnalysisView;
